import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBox, MiSearchInput } from "@miplatform/components";
import { RequirementType } from "@miplatform/types";
import { mdiMinusCircle, mdiPlusCircle } from "@mdi/js";
import { TitledSection } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { RequirementRow } from "./components/RequirementRow";
import { EmptyTransferListView } from "../../../../../components/TransferList/components/EmptyTransferListView";
import { useSelectRequirementsViewModel } from "./useSelectRequirementsViewModel";

const GROUP_LABELS: Record<RequirementType, string> = {
  Inspection: "Inspection Requirements",
  Community: "Community Requirements",
  Builder: "Builder Requirements",
};

const GROUP_ORDER: RequirementType[] = ["Inspection", "Community", "Builder"];

export function SelectRequirements() {
  const { classes, theme } = useStyles();

  const [search, setSearch] = useState("");

  const {
    hasAnyRequirements,
    groupedAvailableRequirements,
    groupedSelectedRequirements,
    materials,
    getRequirementMaterials,
    handleAddRequirement,
    handleRemoveRequirement,
  } = useSelectRequirementsViewModel();

  useEffect(() => {
    if (hasAnyRequirements) {
      getRequirementMaterials();
    }
  }, [hasAnyRequirements]);

  const searchLower = search.trim().toLowerCase();
  const visibleAvailableGroups = GROUP_ORDER.map((type) => ({
    type,
    requirements: groupedAvailableRequirements[type].filter((requirement) =>
      searchLower ? requirement.name.toLowerCase().includes(searchLower) : true
    ),
  }));
  const visibleSelectedGroups = GROUP_ORDER.map((type) => ({
    type,
    requirements: groupedSelectedRequirements[type],
  })).filter((group) => group.requirements.length > 0);

  return (
    <Box className={classes.root}>
      <MiBox headerTitle="Requirements" className={classes.miBox} height="auto">
        <Box className={classes.layout}>
          <TitledSection title="Available Requirements">
            <MiSearchInput
              value={search}
              handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              placeholder="Search"
              style={{ marginLeft: 0 }}
            />
            {hasAnyRequirements ? (
              <Box className={classes.groupsContainer}>
                {visibleAvailableGroups.map(({ type, requirements }) => (
                  <Box key={type} className={classes.group}>
                    <Typography variant="HD1" className={classes.groupLabel}>
                      {GROUP_LABELS[type]}
                    </Typography>
                    <Box className={classes.cardsContainer}>
                      {requirements.map((requirement) => (
                        <RequirementRow
                          key={requirement.id}
                          requirement={requirement}
                          materials={materials}
                          actionIcon={mdiPlusCircle}
                          actionColor={theme.palette.primary.medium}
                          onAction={handleAddRequirement}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <EmptyTransferListView message="There are no available Requirements to choose from." />
            )}
          </TitledSection>
          <TitledSection title="Selected Requirements">
            {visibleSelectedGroups.length > 0 ? (
              <Box className={classes.groupsContainer}>
                {visibleSelectedGroups.map(({ type, requirements }) => (
                  <Box key={type} className={classes.group}>
                    <Typography variant="HD1" className={classes.groupLabel}>
                      {GROUP_LABELS[type]}
                    </Typography>
                    <Box className={classes.cardsContainer}>
                      {requirements.map((requirement) => (
                        <RequirementRow
                          key={requirement.id}
                          requirement={requirement}
                          materials={materials}
                          actionIcon={mdiMinusCircle}
                          actionColor={theme.palette.error.main}
                          onAction={handleRemoveRequirement}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <EmptyTransferListView message="Selected items will show here." />
            )}
          </TitledSection>
        </Box>
      </MiBox>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "hidden",
    height: "100%",
  },
  miBox: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  layout: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: theme.spacing(4),
    flex: 1,
    overflow: "auto",
  },
  groupsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    marginTop: theme.spacing(3),
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  groupLabel: {
    margin: 0,
    color: theme.palette.text.secondary,
  },
  cardsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));
